import React from 'react'
import { Box, Flex, Label, Link, Text } from '@workwhile/ui'
import { CopyableLink } from 'components/Shared/CopyableLink'
import { CopyableCode } from 'components/Shared/CopyableCode'
import { PayoutSummary, UserPaymentStatus } from 'api/payments/getUserPayments'
import { toTitleCase } from 'lib/string'
import { AmountRender } from './AmountRender'
import { formatPaymentTimestamp } from './formatPaymentTimestamp'
import { PaymentStatusRender } from './PaymentStatusRender'
import { PayoutPaymentMethodSummary } from './PayoutPaymentMethodSummary'
import { ProviderDetailLine } from './ProviderDetailLine'
import { RelatedPaymentsTooltipContent } from './RelatedPaymentsTooltipContent'
import { getPayoutStatusLabel } from '../../utils/labels'

type PayoutTooltipContentProps = {
  payout: PayoutSummary
  sourcePaymentId?: number
}

const showPayoutStatusChangedAt = (payout: PayoutSummary): boolean =>
  Boolean(
    payout.statusChangedAt &&
      (payout.status === UserPaymentStatus.COMPLETED ||
        payout.status === UserPaymentStatus.FAILED)
  )

export const PayoutTooltipContent = ({
  payout,
  sourcePaymentId,
}: PayoutTooltipContentProps) => {
  const isCashedOut = payout.status === UserPaymentStatus.COMPLETED
  const payoutLabel = getPayoutStatusLabel(payout.status)

  return (
    <Flex flexDirection="column" gap={2}>
      <Text fontSize="sm" fontWeight="bold">
        <CopyableLink
          label={`#${payout.id}`}
          link={`/user_payments?payment-id=${payout.id}`}
        />
        {' · '}
        {toTitleCase(payout.eventType)}
      </Text>

      <Flex justifyContent="space-between" alignItems="center" gap={2}>
        <Label variant={isCashedOut ? 'success' : 'information'} size="small">
          {payoutLabel}
        </Label>
        <AmountRender value={payout.amount} />
      </Flex>

      <Box>
        <PaymentStatusRender value={payout.status} highnote={payout.highnote} />
        {showPayoutStatusChangedAt(payout) ? (
          <Text fontSize={2} mt={1}>
            {isCashedOut ? 'Sent' : toTitleCase(payout.status)}{' '}
            {formatPaymentTimestamp(payout.statusChangedAt!)}
          </Text>
        ) : null}
      </Box>

      {payout.paymentMethod ?? payout.highnote?.paymentMethod ? (
        <PayoutPaymentMethodSummary
          paymentMethod={
            (payout.paymentMethod ?? payout.highnote?.paymentMethod)!
          }
        />
      ) : null}

      {payout.highnote?.highnoteId ? (
        <ProviderDetailLine label="Highnote ref">
          <CopyableCode value={payout.highnote.highnoteId} />
        </ProviderDetailLine>
      ) : null}

      {payout.evereePaymentId ? (
        <ProviderDetailLine label="Everee payment">
          <Link
            to={`https://app.everee.com/payments/payment/details/${payout.evereePaymentId}`}
            target="_blank"
          >
            {payout.evereePaymentId}
          </Link>
        </ProviderDetailLine>
      ) : null}

      {(payout.relatedPayments ?? []).length > 0 ? (
        <Box pt={2} borderTop="1px solid" borderColor="neutrals.100">
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Bundled payments
          </Text>
          <RelatedPaymentsTooltipContent
            relatedPayments={payout.relatedPayments ?? []}
            payoutAmount={payout.amount}
            highlightPaymentId={sourcePaymentId}
            payoutStatus={payout.status}
          />
        </Box>
      ) : null}
    </Flex>
  )
}
